import { useEffect, useMemo, useState } from 'react';

import { DEBOUNCE_MS } from '@/components/features/UsersList/constants';
import BaseMultiAutocomplete from '@/components/shared/Autocomplete/BaseMultiAutocomplete';
import { BaseMultiAutocompleteProps } from '@/components/shared/Autocomplete/types';
import { useDistributorGroupsAutocomplete } from '@/hooks/autocomplete/useDistributorGroupsAutocomplete';
import { useUserDistributorGroupMembership } from '@/hooks/useUserDistributorGroupMembership';
import type { components } from '@/shared/api/openapi';

type DistributorGroup = components['schemas']['GetDistributorsAutocompleteEndpoint_DistributorsAutocompleteResponse'];

export type EnhancedDistributorGroup = DistributorGroup & {
  isAssignedToUser: boolean;
  role?: string;
};

export interface DistributorGroupAutocompleteForUserProps<EnhancedDistributorGroup> {
  userId: string;
  organizationId: number;
  value: EnhancedDistributorGroup[];
  onChange: BaseMultiAutocompleteProps<EnhancedDistributorGroup>['autocomplete']['onChange'];
  onApply?: NonNullable<BaseMultiAutocompleteProps<EnhancedDistributorGroup>['actionButton']>['onApply'];
  disabled?: boolean;
  buttonRef?: React.RefObject<HTMLButtonElement | null>;
}

function DistributorGroupAutocompleteForUser({
  userId,
  organizationId,
  value,
  onChange,
  onApply,
  disabled,
  buttonRef,
}: DistributorGroupAutocompleteForUserProps<EnhancedDistributorGroup>) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data: groups, isLoading: isLoadingGroups } = useDistributorGroupsAutocomplete({
    organizationId,
    filter: debouncedValue,
    enabled: !disabled,
  });

  const groupIds = useMemo(() => groups?.map((g) => g.systemId).filter((id): id is number => id !== undefined) || [], [groups]);

  const {
    data: membershipData,
    isLoading: isLoadingMembership,
    isRefetching: isRefetchingMembership,
  } = useUserDistributorGroupMembership({
    userId,
    organizationId,
    groupIds,
    enabled: !disabled && groupIds.length > 0,
  });

  const enhancedOptions = useMemo<EnhancedDistributorGroup[]>(() => {
    if (!groups) return [];
    if (isLoadingMembership || isRefetchingMembership) return [];
    if (!membershipData)
      return groups.map((group) => ({
        ...group,
        isAssignedToUser: false,
        role: undefined,
      }));

    const membershipMap = new Map(membershipData.map((m) => [m.groupId, m]));

    return groups.map((group) => {
      const membership = membershipMap.get(group.systemId);
      return {
        ...group,
        isAssignedToUser: membership?.isMember || false,
        role: membership?.role ?? undefined,
      };
    });
  }, [groups, membershipData, isLoadingMembership, isRefetchingMembership]);

  const handleInputChange = (_: React.SyntheticEvent, input: string) => {
    setInputValue(input);
  };

  const getOptionLabel = (option: EnhancedDistributorGroup) => option.distributorName || '';

  const getOptionDisabled = (optionLabel: EnhancedDistributorGroup) => {
    if (isLoadingMembership || isRefetchingMembership || !membershipData) {
      return false;
    }

    const isSelectedOptionDisabled = value.some((val) => val.distributorName === optionLabel.distributorName);

    const option = enhancedOptions.find((g) => g.distributorName === optionLabel.distributorName);
    return option?.isAssignedToUser || isSelectedOptionDisabled;
  };

  return (
    <BaseMultiAutocomplete<EnhancedDistributorGroup>
      autocomplete={{
        options: enhancedOptions,
        value: value,
        loading: isLoadingGroups || isLoadingMembership || isRefetchingMembership,
        onChange: onChange,
        onInputChange: handleInputChange,
        getOptionLabel: getOptionLabel,
        getOptionDisabled: getOptionDisabled,
        label: 'Search Group to Add',
        placeholder: 'Enter at least 3 characters to search',
        disabled: disabled ?? false,
      }}
      actionButton={{
        onApply: onApply,
        buttonLabel: 'Add',
        buttonRef: buttonRef,
      }}
    />
  );
}

export default DistributorGroupAutocompleteForUser;
